import type { CSSProperties } from 'react';
import {
  Link,
  ProgressBar,
  Toast,
  ToastTitle,
  ToastTrigger,
  makeStyles,
  progressBarClassNames,
  tokens,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import {
  NOTIFY_VARIANTS,
  type NotifyShowOptions,
  type NotifyVariant,
} from './config';

const useStyles = makeStyles({
  toast: {
    position: 'relative',
    overflow: 'hidden',
    ':hover': {
      [`& .${progressBarClassNames.bar}`]: {
        animationPlayState: 'paused',
      },
    },
  },
  action: {
    marginLeft: tokens.spacingHorizontalS,
  },
  progress: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    [`& .${progressBarClassNames.bar}`]: {
      transformOrigin: 'left center',
      animationName: {
        from: { transform: 'scaleX(1)' },
        to: { transform: 'scaleX(0)' },
      },
      animationTimingFunction: 'linear',
      animationFillMode: 'forwards',
      animationDuration: 'var(--toast-timeout)',
    },
  },
});

const NotifyToast = ({
  variant,
  message,
  timeoutMs,
  action,
}: {
  variant: NotifyVariant;
  message: string;
  timeoutMs: number | null;
  action?: NotifyShowOptions['action'];
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const config = NOTIFY_VARIANTS[variant];

  return (
    <Toast
      className={styles.toast}
      style={
        {
          backgroundColor: config.backgroundColor,
          border: `${tokens.strokeWidthThin} solid ${config.borderColor}`,
          ...(timeoutMs != null
            ? { '--toast-timeout': `${timeoutMs}ms` }
            : undefined),
        } as CSSProperties
      }
    >
      <ToastTitle
        action={
          <ToastTrigger>
            <Link aria-label={t('close', 'Close')}>
              <Dismiss20Regular />
            </Link>
          </ToastTrigger>
        }
      >
        {message}
        {action ? (
          <Link
            inline
            className={styles.action}
            onClick={(event) => {
              event.preventDefault();
              action.onClick();
            }}
          >
            {action.label}
          </Link>
        ) : null}
      </ToastTitle>
      {timeoutMs != null && (
        <ProgressBar
          className={styles.progress}
          thickness="medium"
          color={config.progressColor}
          value={1}
        />
      )}
    </Toast>
  );
};

export default NotifyToast;
